// Percorso file: src/utils/categoria.js
//
// LA CATEGORIA ANAGRAFICA DI UNA PERSONA — SEDE UNICA (doc 07 §2, §8.1
// «Aggiunta 26 agosto 2026 — Admin User List Dashboard»).
//
// Risponde a una domanda sola: «di quale delle quattro categorie è questa
// persona, nell'annata che sto guardando?» — MEN, BOYS, WOMEN, GIRLS.
// MIXED non compare: è categoria di SQUADRA, non di persona (doc 07 §2).
//
// ⚠️ NON È LA CATEGORIA DELLA SQUADRA, ed è la differenza che conta.
// Altrove nell'app la stessa pastiglia dice la categoria del TEAM, letta
// dai documenti delle squadre: `categoriaDi(u.team)` in TeamAssignment.jsx
// e la lettura di `GET /teams` in MemberProfileModal.jsx. Qui non si legge
// nessuna squadra: si guardano il genere e la data di nascita della
// persona. Le due letture convivono finché non le riconcilia il cantiere
// che porterà `category` in proiezione dal backend (diario, P8).
//
// ⚠️ SI MOSTRA, NON SI SALVA. Nessuna delle due funzioni tocca il
// documento, nessuna chiama la rete: ricevono un valore e ne restituiscono
// un altro.
//
// Sta in `utils/` e non dentro UsersList.jsx perché la domanda non è di
// quella pagina: il giorno in cui la stessa pastiglia servirà altrove, la
// regola dev'essere una sola (CLAUDE.md: sede unica).

// L'età compiuta e il calendario di Perth vengono dalla sede unica già in
// opera — la stessa che usano Edit Member e View Profile — e non si
// riscrivono qui: due conti dell'età divergerebbero nel giorno del
// compleanno, che è esattamente il giorno in cui devono coincidere.
import { etaCompiuta, oggiAPerth } from "./enrolDati";

/** Doc 07 §8.1: la soglia è SPORTIVA (17), e non ha nulla a che vedere con
 *  la minore età legale (18) di `isMinor` / `isMinorDaDob`. Sono due
 *  domande diverse e non si derivano l'una dall'altra. */
export const SOGLIA_CATEGORIA = 17;

/**
 * Il genere in forma canonica: "M", "F", oppure "" se non si sa.
 *
 * ⚠️ TOLLERANTE ALLE DUE GRAFIE IN CIRCOLAZIONE, e non è pignoleria:
 *   - le rotte d'iscrizione del modello nuovo IMPONGONO "M"/"F"
 *     (server.js): ogni socio nato in beta ha la lettera;
 *   - l'importazione storica scriveva il valore grezzo del foglio
 *     (`u['Male or Female']`, server.js): lì si trova «Male»/«Female».
 * Un lettore che conoscesse una sola delle due tacerebbe su metà anagrafica.
 * Qualunque altro valore (vuoto, «Other», un refuso) vale "": non si tira a
 * indovinare il genere di nessuno.
 */
export function genereCanonico(valore) {
  const g = String(valore || "").trim().toUpperCase();

  if (g === "M" || g === "MALE") return "M";
  if (g === "F" || g === "FEMALE") return "F";

  return "";
}

/**
 * La categoria della persona nell'annata indicata: "MEN" | "BOYS" |
 * "WOMEN" | "GIRLS", oppure "" quando non si può dire.
 *
 * @param persona documento di lista (servono `gender` e `dob`, entrambi già
 *                nella proiezione di lista del backend — la funzione
 *                `proiezioneDiLista` di `backend/server.js`, citata per
 *                contenuto e non per numero di riga, che slitta a ogni
 *                fetta)
 * @param annata  l'anno della stagione sportiva (intero). Se non è un
 *                intero — «All seasons» con la cassettina non ancora
 *                arrivata — si ripiega sulla data di oggi.
 *
 * ⚠️ L'ETÀ SI MISURA AL 31 DICEMBRE DELL'ANNATA, non alla data di oggi
 * (decisione dell'utente del 26/8/2026, doc 07 §8.1). La categoria è «per
 * stagione sportiva»: misurandola sull'oggi, la pastiglia di un socio
 * cambierebbe da BOYS a MEN a stagione in corso, il giorno del suo
 * diciassettesimo compleanno.
 *
 * ⚠️ Il vuoto è una risposta legittima e va restituito, non mascherato: chi
 * non ha genere o non ha una data di nascita leggibile non riceve una
 * pastiglia (il componente `PastigliaCategoriaSeNota` tace da sé). Un
 * timbro inventato sarebbe peggio del silenzio.
 */
export function categoriaAnagrafica(persona, annata) {
  const genere = genereCanonico(persona && persona.gender);
  if (!genere) return "";

  const riferimento = Number.isInteger(annata)
    ? `${annata}-12-31`
    : oggiAPerth();

  const eta = etaCompiuta(persona && persona.dob, riferimento);
  if (eta === null) return "";

  const adulto = eta >= SOGLIA_CATEGORIA;

  if (genere === "M") return adulto ? "MEN" : "BOYS";
  return adulto ? "WOMEN" : "GIRLS";
}
